import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {SubmitHandler, useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {useQueryClient} from '@tanstack/react-query';
import {useDispatch, useSelector} from 'react-redux';
import deepEqual from 'deep-equal';
import {QUERY_KEYS} from '../../../../../utils/queryKeys';
import Modal from '../../../../../incase-domain/components/modals/directory/Modal';
import VerticalNavigation from '../../../../../incase-domain/components/verticalNavigation/VerticalNavigation';
import Button from '../../../../../incase-domain/components/button/Button';
import {IPresentationMeta} from '../../../../../incase-domain/presentation';
import {PlayerLabel} from '../../../../../incase-domain/playerLabel';
import GeneralSettings from './GeneralSettings';
import LocaleSettings, {initLocaleLabels} from './LocaleSettings';
import {
    PresentationSettingsFormType,
    presentationSettingsSchema,
} from '../../../../../incase-domain/validators/presentation/presentationSchema';
import AccessLinksSettings from './AccessLinksSettings';
import GamificationSettings from './GamificationSettings';
import CollaborateSettings from './CollaborateSettings';
import PresentationThemeSettingsForm from '../../../../../components/presentation/settings/PresentationThemeSettingsForm';
import useUpdatePresentation from '../../../../../service/hooks/useUpdatePresentation';
import {
    fetchPresentationProtectionAction,
    setPresentationSettingsModalAction,
    updatePresentationProtectionAction,
} from '../../../../../store/actions/presentation';
import PresentationProtectionSettingsForm from '../../../../../components/presentation/settings/PresentationProtectionSettingsForm';
import {presentationProtectionSelector, presentationSettingsModalSelector} from '../../../../../store/selectors/presentation.selectors';

interface PresentationSettingsModalProps {
    presentation: IPresentationMeta;
}

function PresentationSettingsModal({presentation}: PresentationSettingsModalProps): JSX.Element | null {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const protection = useSelector(presentationProtectionSelector);
    const settingsModal = useSelector(presentationSettingsModalSelector);

    const [activeTab, setActiveTab] = useState(0);
    const [labels, setLabels] = useState<PlayerLabel[]>(() => initLocaleLabels(presentation.languageConfig?.labels));

    const formMethods = useForm<PresentationSettingsFormType>({
        defaultValues: {
            presentation,
            protection,
        },
        resolver: zodResolver(presentationSettingsSchema),
    });

    const {mutate: updatePresentation} = useUpdatePresentation();

    const {handleSubmit, formState, setValue, watch, reset} = formMethods;

    useEffect(() => {
        if (settingsModal.open && presentation._id && protection._id !== presentation._id) {
            dispatch(fetchPresentationProtectionAction(presentation._id));
        }
    }, [presentation._id, dispatch, protection, settingsModal.open]);

    useEffect(() => {
        if (protection?._id) {
            setValue('protection', protection);
        }
    }, [protection, setValue]);

    useEffect(() => {
        if (settingsModal.tab !== undefined) {
            setActiveTab(settingsModal.tab);
        }
    }, [settingsModal.tab]);

    useEffect(() => {
        if (settingsModal.open) {
            const currentFormValues = watch();

            if (protection?._id && !deepEqual(currentFormValues.protection, protection)) {
                reset({
                    ...currentFormValues,
                    protection,
                });
            }

            if (!deepEqual(currentFormValues.presentation, presentation)) {
                reset({
                    ...currentFormValues,
                    presentation,
                });
            }

            setLabels(initLocaleLabels(presentation.languageConfig?.labels));
        }
    }, [settingsModal.open, protection, presentation, watch, reset]);

    const onSubmit: SubmitHandler<PresentationSettingsFormType> = (data) => {
        updatePresentation({presentation: {...data.presentation, languageConfig: {labels}}});
        dispatch(updatePresentationProtectionAction(data.presentation._id, data.protection));
        queryClient.invalidateQueries({queryKey: QUERY_KEYS.presentation.lists()});
        dispatch(setPresentationSettingsModalAction(false, 0));
    };

    const handleCloseModal = (): void => {
        dispatch(setPresentationSettingsModalAction(false, 0));
        formMethods.reset();
    };

    if (!settingsModal.open) return null;

    const tabItems = [
        {
            label: t('presEditPage.settingsModal.general'),
            icon: 'solar:presentation-graph-linear',
            content: <GeneralSettings useForm={formMethods} />,
        },
        {
            label: t('presEditPage.settingsModal.protection'),
            icon: 'solar:lock-keyhole-linear',
            content: <PresentationProtectionSettingsForm useForm={formMethods} />,
        },
        {
            label: t('presEditPage.settingsModal.accessLinks'),
            icon: 'solar:link-linear',
            content: <AccessLinksSettings useForm={formMethods} />,
        },
        {
            label: t('presEditPage.settingsModal.theme'),
            icon: 'solar:pallete-2-linear',
            content: <PresentationThemeSettingsForm useForm={formMethods} />,
        },
        {
            label: t('presEditPage.settingsModal.gamification'),
            icon: 'solar:gamepad-minimalistic-linear',
            content: <GamificationSettings useForm={formMethods} />,
        },
        {
            label: t('presEditPage.settingsModal.collaborate'),
            icon: 'solar:users-group-rounded-linear',
            content: <CollaborateSettings useForm={formMethods} />,
        },
        {
            label: t('presEditPage.settingsModal.locale'),
            icon: 'solar:text-field-linear',
            content: <LocaleSettings labels={labels} onChange={setLabels} />,
        },
    ];

    return (
        <Modal
            isOpen={settingsModal.open}
            header={presentation.name}
            description={t('presEditPage.settings.general.title')}
            icon="solar:settings-bold"
            size="lg"
            noPadding
            closeOnClickOutside={false}
            onCancel={handleCloseModal}
        >
            <form onSubmit={handleSubmit(onSubmit)} className="c-editRoomModal">
                <div className="c-editRoomModal__navigation">
                    <VerticalNavigation
                        items={tabItems.map(({label, icon}) => ({label, icon}))}
                        onChange={(index) => setActiveTab(index)}
                        initialIndex={activeTab}
                    />
                </div>

                <div className="c-editRoomModal__content">
                    <div className="c-editRoomModal__content_body">{tabItems[activeTab].content}</div>

                    <div className="c-editRoomModal__content_footer">
                        <Button variant="solid" size="sm" color="info" wide loading={formState.isSubmitting} type="submit">
                            {t('common.save')}
                        </Button>
                    </div>
                </div>
            </form>
        </Modal>
    );
}

export default PresentationSettingsModal;
